import { createContext, useCallback, useContext } from 'react'

export type PageSidePanelSide = 'start' | 'end'

/**
 * Where a page's side panels are drawn: on the app's frame beside the page
 * card, so every side panel is a card of its own rather than a panel inside
 * the page. `start` is between the rail and the page card (the Tables and
 * Views lists), `end` is right of the page card, before AI Assistant and
 * Support (Record Preview). The app's layouts provide the spots;
 * `PageSidePanel` draws its panel there. With no spot (a test, or a page
 * outside the layouts), the panel stays where it is rendered.
 */
export interface PageSidePanelSlots {
  elements: Record<PageSidePanelSide, HTMLElement | null>
  /** The layout's callback ref for each spot. */
  setElement: (side: PageSidePanelSide, element: HTMLElement | null) => void
}

export const PageSidePanelSlotsContext = createContext<PageSidePanelSlots | null>(null)

/** The element a page's side panel should be drawn into, or null. */
export function usePageSidePanelSlot(side: PageSidePanelSide): HTMLElement | null {
  return useContext(PageSidePanelSlotsContext)?.elements[side] ?? null
}

/**
 * The layout's callback ref for a spot (a no-op outside a layout). It is
 * stable, so React doesn't detach and reattach the spot on every render.
 */
export function usePageSidePanelSlotRef(side: PageSidePanelSide) {
  const setElement = useContext(PageSidePanelSlotsContext)?.setElement
  return useCallback(
    (element: HTMLElement | null) => setElement?.(side, element),
    [setElement, side]
  )
}
